import * as React from 'react';
import { ChevronDown, Pencil } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

/**
 * Shared field tokens and primitives for gate-flow forms (auth modal, enquiry
 * gate and future gated surfaces) so every flow that includes the auth gate
 * renders identical fields. Values are the exact Figma gating-frame tokens —
 * see epics/BC-4826-login-signup-modal-revamp/design.md.
 */
export const GATE_FIELD_LABEL_CLASS =
    'text-[0.75rem] font-medium uppercase tracking-[-0.02em] text-[#161616]';

/**
 * Chrome/Safari paint a pale blue wash on autofilled fields that cannot be
 * overridden with `background-color`. An inset shadow matching the field grey
 * covers it; text-fill keeps the typed colour so the shadow does not wash
 * the glyphs.
 */
const GATE_FIELD_AUTOFILL_CLASS =
    '[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#f6f6f6] [&:-webkit-autofill:hover]:shadow-[inset_0_0_0_1000px_#f6f6f6] [&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_#f6f6f6] [&:-internal-autofill-selected]:shadow-[inset_0_0_0_1000px_#f6f6f6] [&:-webkit-autofill]:[-webkit-text-fill-color:#161616]';

/**
 * Every focusable control in the gate holds a 16px floor.
 *
 * iOS Safari zooms the page whenever a focused control computes under 16px, and
 * the zoom moves the visual viewport — which is the signal the sheet reads to
 * follow the keyboard. So the field is not merely uncomfortable to read: it
 * makes the sheet chase a viewport that the browser is animating.
 *
 * `rem` is not a defence here. The web root is `3.865vw`, which is 16px only at
 * 414px wide; on a 390px iPhone `1rem` is 15.07px and on a 375px one 14.49px —
 * under the threshold on every iPhone but the largest.
 *
 * The floor is released at `md` because the root is fluid at both ends: it is
 * `0.833vw` from `lg`, so `1rem` is 12px on a 1440px laptop and a floor left on
 * would render the field a third larger than designed. Below 414px the `max()`
 * binds and above it the design size already wins, so the only widths that
 * change are the ones that would have zoomed.
 *
 * `font-normal` overrides the weight Rocco's Input bakes into its size variant.
 * What the reader typed is content, not emphasis — at 500 it competed with the
 * labels above it and read heavier than the design.
 */
export const GATE_FIELD_INPUT_CLASS = `h-10.5 rounded-[1rem] border-0 bg-[#f6f6f6] px-2.5 text-[max(1rem,16px)] font-normal tracking-[-0.02em] text-[#161616] placeholder:font-normal placeholder:text-black/30 focus:outline-none focus:ring-1 focus:ring-black/15 md:text-[1rem] ${GATE_FIELD_AUTOFILL_CLASS}`;

export const GateFieldLabel = ({
    className,
    ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) => (
    <label className={cn(GATE_FIELD_LABEL_CLASS, className)} {...props} />
);

export const GateInput = React.forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement> & {
        /**
         * Mark a field the reader may already find filled in — name, email,
         * phone — with a pencil once it holds a value.
         *
         * These fields arrive prefilled from the member's profile, and a filled
         * grey box with no border reads as a value being shown rather than one
         * being asked for. A reader who believes a stale phone number is
         * read-only either abandons or sends it as-is, which is a worse lead
         * than one they corrected. Leave it off for fields carrying their own
         * trailing control — a second glyph reads as two actions.
         */
        editHint?: boolean;
    }
>(function GateInput({ className, editHint = false, ...props }, ref) {
    const input = (
        <input
            ref={ref}
            className={cn(
                GATE_FIELD_INPUT_CLASS,
                'w-full',
                editHint && 'peer pr-9',
                className,
            )}
            {...props}
        />
    );

    if (!editHint) return input;

    return (
        <div className="relative">
            {input}
            {/* Driven by `:placeholder-shown`, so it appears only once the
                field holds something — an empty field already looks like one.
                Hidden on focus: by then the reader is editing and the cursor
                says so. */}
            <Pencil
                aria-hidden
                className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-black/30 transition-opacity peer-placeholder-shown:opacity-0 peer-focus:opacity-0"
            />
        </div>
    );
});

export const GateTextarea = React.forwardRef<
    HTMLTextAreaElement,
    React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function GateTextarea({ className, ...props }, ref) {
    return (
        <textarea
            ref={ref}
            className={cn(
                GATE_FIELD_INPUT_CLASS,
                'h-auto w-full resize-none py-3 leading-[1.3]',
                className,
            )}
            {...props}
        />
    );
});

export const GateSelect = React.forwardRef<
    HTMLSelectElement,
    React.SelectHTMLAttributes<HTMLSelectElement>
>(function GateSelect({ className, children, ...props }, ref) {
    return (
        <div className="relative">
            <select
                ref={ref}
                className={cn(
                    GATE_FIELD_INPUT_CLASS,
                    'w-full appearance-none pr-8',
                    className,
                )}
                {...props}
            >
                {children}
            </select>
            <ChevronDown className="pointer-events-none absolute inset-y-0 right-2.5 my-auto size-4 text-black/50" />
        </div>
    );
});

export const GateFieldError = ({ message }: { message?: string }) =>
    message ? (
        <p role="alert" className="mt-1 text-[0.6875rem] text-destructive">
            {message}
        </p>
    ) : null;
